/**
 * FeedbackButton.jsx
 *
 * Floating feedback / support button. Sits in the bottom-right corner of the app.
 * Opens a small modal with a message form. Sends via /api/send-feedback (Resend).
 *
 * Usage in App.jsx — drop this just before the closing </div> of the main app render:
 *   import FeedbackButton from './FeedbackButton';
 *   <FeedbackButton userEmail={session.user.email} />
 */

import { useState } from "react";
import { APP_NAME } from './brand';

const TYPES = [
  { value: "bug",     label: "Bug report" },
  { value: "feature", label: "Feature request" },
  { value: "account", label: "Account / billing" },
  { value: "other",   label: "Something else" },
];

export default function FeedbackButton({ userEmail = "" }) {
  const [open, setOpen]       = useState(false);
  const [type, setType]       = useState("bug");
  const [message, setMessage] = useState("");
  const [visitorEmail, setVisitorEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus]   = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSend() {
    if (!message.trim()) return;
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch("/api/send-feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, message: message.trim(), userEmail: userEmail || visitorEmail.trim(), website }),
      });
      if (res.ok) {
        setStatus("sent");
        setTimeout(() => {
          setOpen(false);
          setStatus("idle");
          setMessage("");
          setVisitorEmail("");
          setType("bug");
        }, 2200);
      } else {
        const d = await res.json().catch(() => ({}));
        setErrorMsg(d.error || "Could not send. Try again.");
        setStatus("error");
      }
    } catch {
      setErrorMsg("Connection error. Try again.");
      setStatus("error");
    }
  }

  function handleClose() {
    if (status === "sending") return;
    setOpen(false);
    setStatus("idle");
    setErrorMsg("");
  }

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(true)}
        title="Feedback &amp; support"
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 200,
          width: 44, height: 44, borderRadius: "50%",
          background: "#0e1e21", border: "1px solid #1a383e",
          color: "#6e93a3", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 18, lineHeight: 1,
          transition: "border-color .15s, color .15s, transform .15s",
          boxShadow: "0 2px 12px rgba(0,0,0,0.5)",
          fontFamily: "inherit",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = "#1fd8f5";
          e.currentTarget.style.color = "#1fd8f5";
          e.currentTarget.style.transform = "scale(1.08)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = "#1a383e";
          e.currentTarget.style.color = "#316d79";
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        {/* Chat bubble icon via SVG */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      </button>

      {/* Backdrop */}
      {open && (
        <div
          onClick={handleClose}
          style={{
            position: "fixed", inset: 0, zIndex: 300,
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(2px)",
          }}
        />
      )}

      {/* Modal */}
      {open && (
        <div style={{
          position: "fixed", bottom: 80, right: 24, zIndex: 301,
          width: 320, maxWidth: "calc(100vw - 48px)",
          background: "#0a171a",
          border: "1px solid #13292d",
          borderTop: "2px solid #1fd8f5",
          borderRadius: 6,
          padding: "24px 22px",
          fontFamily: "'Inter', sans-serif",
          boxShadow: "0 8px 40px rgba(0,0,0,0.7)",
        }}>

          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: 9, letterSpacing: 3, color: "#1fd8f5", textTransform: "uppercase", marginBottom: 3 }}>
                {APP_NAME} Support
              </div>
              <div style={{ fontSize: 22, color: "#f0f0f2", fontFamily: "'Bebas Neue', Impact, sans-serif", letterSpacing: 1 }}>
                Get in touch
              </div>
            </div>
            <button
              onClick={handleClose}
              style={{ background: "none", border: "none", color: "#6e93a3", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0 }}
            >
              &times;
            </button>
          </div>

          {status === "sent" ? (
            /* Success state */
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <div style={{ fontSize: 28, marginBottom: 10, color: "#1fd8f5" }}>&#10003;</div>
              <div style={{ fontSize: 12, color: "#f0f0f2", marginBottom: 6 }}>Message sent.</div>
              <div style={{ fontSize: 11, color: "#6e93a3" }}>{(userEmail || visitorEmail) ? <>We'll get back to you at<br />{userEmail || visitorEmail}</> : "Thanks for letting us know."}</div>
            </div>
          ) : (
            <>
              {/* Type selector */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, letterSpacing: 2, color: "#6e93a3", textTransform: "uppercase", marginBottom: 6 }}>
                  Type
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {TYPES.map(t => (
                    <button
                      key={t.value}
                      onClick={() => setType(t.value)}
                      style={{
                        background: type === t.value ? "#0a3a45" : "#081315",
                        border: `1px solid ${type === t.value ? "#1fd8f560" : "#1a383e"}`,
                        color: type === t.value ? "#1fd8f5" : "#316d79",
                        borderRadius: 3, padding: "5px 10px",
                        fontSize: 10, cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "all .12s",
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, letterSpacing: 2, color: "#6e93a3", textTransform: "uppercase", marginBottom: 6 }}>
                  Message
                </div>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Describe what happened, what you expected, or what you'd like to see..."
                  rows={4}
                  style={{
                    width: "100%", background: "#081315",
                    border: "1px solid #1a383e", color: "#f0f0f2",
                    borderRadius: 4, padding: "10px 12px",
                    fontSize: 12, outline: "none", resize: "vertical",
                    fontFamily: "inherit", lineHeight: 1.7,
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Reply-to */}
              {userEmail ? (
                <div style={{ fontSize: 10, color: "#6e93a3", marginBottom: 14 }}>
                  Reply will go to: <span style={{ color: "#6e93a3" }}>{userEmail}</span>
                </div>
              ) : (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 9, letterSpacing: 2, color: "#6e93a3", textTransform: "uppercase", marginBottom: 6 }}>
                    Your email (optional, if you'd like a reply)
                  </div>
                  <input
                    type="email"
                    value={visitorEmail}
                    onChange={e => setVisitorEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    style={{
                      width: "100%", background: "#081315",
                      border: "1px solid #1a383e", color: "#f0f0f2",
                      borderRadius: 4, padding: "10px 12px",
                      fontSize: 12, outline: "none",
                      fontFamily: "inherit", boxSizing: "border-box",
                    }}
                  />
                </div>
              )}

              {/* Honeypot: hidden from people, bots fill it in */}
              <input
                type="text" tabIndex={-1} autoComplete="off" aria-hidden="true"
                value={website} onChange={e => setWebsite(e.target.value)}
                style={{ position: "absolute", left: -9999, width: 1, height: 1, opacity: 0 }}
              />

              {/* Error */}
              {status === "error" && (
                <div style={{ fontSize: 11, color: "#ff6b6b", marginBottom: 12, padding: "8px 10px", background: "#1f0000", borderRadius: 4 }}>
                  {errorMsg}
                </div>
              )}

              {/* Send */}
              <button
                onClick={handleSend}
                disabled={status === "sending" || !message.trim()}
                style={{
                  width: "100%", background: "#1fd8f5", border: "none",
                  color: "#031a20", borderRadius: 4, padding: "11px 16px",
                  fontSize: 10, letterSpacing: 2, textTransform: "uppercase",
                  cursor: status === "sending" || !message.trim() ? "default" : "pointer",
                  fontFamily: "inherit",
                  opacity: status === "sending" || !message.trim() ? 0.5 : 1,
                  transition: "opacity .15s",
                }}
              >
                {status === "sending" ? "Sending..." : "Send Message"}
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
